import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  HStack,
  Icon,
  VStack,
  Spinner,
  Center,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import { MdStar, MdStarHalf, MdStarOutline } from "react-icons/md";
import { restaurantService } from "../../../../services/restaurantService";

export default function SimpleFeedback({ feedbackData = null }) {
  const [loading, setLoading] = useState(true);
  const [restaurantFeedback, setRestaurantFeedback] = useState(null);
  
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const bgComment = useColorModeValue("gray.50", "navy.700");
  
  // RestaurantService'den verileri çek
  useEffect(() => {
    const fetchRestaurantStats = async () => {
      try {
        setLoading(true);
        const response = await restaurantService.getRestaurantStats();
        if (response.success) {
          
          setRestaurantFeedback({
            averageRating: response.data.rating.averageRating,
            totalRatings: response.data.rating.totalRatings,
            recentReviews: response.data.rating.reviews.map(review => {
              // userId nesnesinden müşteri bilgilerini al
              const customer = review.userId || {};
              const customerName = customer.name && customer.surname 
                ? `${customer.name} ${customer.surname}`
                : customer.name || "Misafir";
                
              return {
                customerName: formatCustomerName(customerName),
                rating: review.rating,
                comment: review.comment,
                date: formatReviewDate(review.createdAt)
              };
            })
          });
        }
      } catch (error) {
        console.error("Müşteri değerlendirmeleri yüklenirken hata oluştu:", error);
      } finally {
        setLoading(false);
      }
    };

    // Eğer feedbackData prop'u verilmemişse, API'den verileri çek
    if (!feedbackData) {
      fetchRestaurantStats();
    } else {
      setRestaurantFeedback({
        ...feedbackData,
        recentReviews: feedbackData.recentReviews?.map(review => ({
          ...review,
          customerName: formatCustomerName(review.customerName || "Misafir")
        }))
      });
      setLoading(false);
    }
  }, [feedbackData]);

  // Müşteri ismini gizlilik kurallarına göre formatla
  const formatCustomerName = (fullName) => {
    if (!fullName || fullName === "Misafir") return "Misafir";
    
    const nameParts = fullName.split(' ');
    if (nameParts.length === 1) {
      // Sadece tek isim varsa
      const name = nameParts[0];
      if (name.length <= 2) return name;
      return `${name.charAt(0)}${'*'.repeat(name.length - 1)}`;
    } else {
      // İsim ve soyisim varsa
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      
      // İlk ismin ilk harfi + yıldızlar + soyismin ilk harfi + yıldızlar
      const maskedFirstName = `${firstName.charAt(0)}${'*'.repeat(Math.max(1, firstName.length - 1))}`;
      const maskedLastName = `${lastName.charAt(0)}${'*'.repeat(Math.max(1, lastName.length - 1))}`;
      
      return `${maskedFirstName} ${maskedLastName}`;
    }
  };

  // Tarih formatını düzenle
  const formatReviewDate = (dateString) => {
    if (!dateString) return "Bilinmeyen tarih";
    
    const reviewDate = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - reviewDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return "Bugün";
    } else if (diffDays === 1) {
      return "Dün";
    } else if (diffDays < 7) {
      return `${diffDays} gün önce`;
    } else if (diffDays < 30) {
      return `${Math.floor(diffDays / 7)} hafta önce`;
    } else {
      return reviewDate.toLocaleDateString('tr-TR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  };
  
  // Yıldız gösterimi için
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Icon as={MdStar} key={i} color="yellow.400" w="16px" h="16px" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<Icon as={MdStarHalf} key={i} color="yellow.400" w="16px" h="16px" />);
      } else {
        stars.push(<Icon as={MdStarOutline} key={i} color="yellow.400" w="16px" h="16px" />);
      }
    }
    
    return stars;
  };

  if (loading) {
    return (
      <Card>
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4">
          Müşteri Değerlendirmeleri
        </Text>
        <Center py={8}>
          <Spinner color="brand.500" />
        </Center>
      </Card>
    );
  }

  // Veri yoksa veya hata varsa
  if (!restaurantFeedback || !restaurantFeedback.averageRating) {
    return (
      <Card>
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4">
          Müşteri Değerlendirmeleri
        </Text>
        <Center py={8}>
          <Text>Henüz değerlendirme bulunmuyor.</Text>
        </Center>
      </Card>
    );
  }

  return (
    <Card h="100%">
      <Text color={textColor} fontSize="lg" fontWeight="700" mb="4">
        Müşteri Değerlendirmeleri
      </Text>
      
      <Flex align="center" mb={4}>
        <Text fontSize="3xl" fontWeight="bold" mr={2}>
          {restaurantFeedback.averageRating.toFixed(1)}
        </Text>
        <HStack spacing={0}>
          {renderStars(restaurantFeedback.averageRating)}
        </HStack>
      </Flex>
      
      <Text fontSize="sm" mb={4}>
        Toplam {restaurantFeedback.totalRatings} değerlendirme
      </Text>
      
      {restaurantFeedback.recentReviews && restaurantFeedback.recentReviews.length > 0 ? (
        <>
          <Text fontWeight="medium" mb={2}>Son Yorumlar</Text>
          <Box maxH="450px" overflowY="auto" pr={2}>
            <VStack spacing={3} align="stretch">
              {restaurantFeedback.recentReviews.map((review, index) => (
                <Box key={index} p={3} borderRadius="md" bg={bgComment}>
                  <Flex justify="space-between" mb={1}>
                    <Text fontWeight="medium">{review.customerName}</Text>
                    <HStack spacing={0}>
                      {renderStars(review.rating)}
                    </HStack>
                  </Flex>
                  <Text fontSize="sm">{review.comment}</Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>{review.date}</Text>
                </Box>
              ))}
            </VStack>
          </Box>
        </>
      ) : (
        <Text>Henüz yorum bulunmuyor.</Text>
      )}
    </Card>
  );
} 